import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { PeriodoPlanilla } from '../types/periodo';
import { PeriodoService } from '../services';

interface PeriodoState {
  periodos: PeriodoPlanilla[];
  loading: boolean;
  error: string | null;
  selectedPeriodo: PeriodoPlanilla | null;
  periodoActivo: PeriodoPlanilla | null;
  dialogOpen: boolean;

  fetchPeriodos: () => Promise<void>;
  fetchPeriodoActivo: () => Promise<void>;
  createPeriodo: (periodo: PeriodoPlanilla) => Promise<void>;
  updateEstado: (id: number, estado: string) => Promise<void>;
  setSelectedPeriodo: (periodo: PeriodoPlanilla | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
}

export const usePeriodoStore = create<PeriodoState>()(
  devtools(
    (set) => ({
      periodos: [],
      loading: false,
      error: null,
      selectedPeriodo: null,
      periodoActivo: null,
      dialogOpen: false,

      fetchPeriodos: async () => {
        set({ loading: true, error: null }, false, 'periodos/fetch/start');
        try {
          const periodos = await PeriodoService.getAll();
          set({ periodos, loading: false }, false, 'periodos/fetch/success');
        } catch (error) {
          set(
            { loading: false, error: error instanceof Error ? error.message : 'Error al cargar periodos' },
            false,
            'periodos/fetch/error'
          );
        }
      },

      fetchPeriodoActivo: async () => {
        try {
          const periodoActivo = await PeriodoService.getActivo();
          set({ periodoActivo }, false, 'periodos/fetchActivo/success');
        } catch {
          // Silencioso - no hay periodo activo
        }
      },

      createPeriodo: async (periodo: PeriodoPlanilla) => {
        set({ loading: true, error: null }, false, 'periodos/create/start');
        try {
          const created = await PeriodoService.create(periodo);
          set(
            (state) => ({
              periodos: [created, ...state.periodos],
              loading: false,
              dialogOpen: false,
              selectedPeriodo: null,
            }),
            false,
            'periodos/create/success'
          );
        } catch (error: unknown) {
          const errorObj = error as Record<string, unknown>;
          const errorMessage =
            (errorObj?.userMessage as string) ||
            (errorObj?.message as string) ||
            (error instanceof Error ? error.message : 'Error al crear periodo');
          set(
            { loading: false, error: errorMessage },
            false,
            'periodos/create/error'
          );
          throw error;
        }
      },

      updateEstado: async (id: number, estado: string) => {
        set({ loading: true, error: null }, false, 'periodos/updateEstado/start');
        try {
          const updated = await PeriodoService.updateEstado(id, estado);
          set(
            (state) => ({
              periodos: state.periodos.map(p => p.idPeriodo === id ? updated : p),
              loading: false,
              periodoActivo: updated.estado === 'ABIERTO' ? updated : state.periodoActivo,
            }),
            false,
            'periodos/updateEstado/success'
          );
        } catch (error: unknown) {
          const errorObj = error as Record<string, unknown>;
          const errorMessage =
            (errorObj?.userMessage as string) ||
            (errorObj?.message as string) ||
            (error instanceof Error ? error.message : 'Error al cambiar estado');
          set(
            { loading: false, error: errorMessage },
            false,
            'periodos/updateEstado/error'
          );
        }
      },

      setSelectedPeriodo: (periodo) => {
        set({ selectedPeriodo: periodo }, false, 'periodos/setSelected');
      },

      openDialog: () => {
        set({ dialogOpen: true }, false, 'periodos/openDialog');
      },

      closeDialog: () => {
        set({ dialogOpen: false, selectedPeriodo: null }, false, 'periodos/closeDialog');
      },

      clearError: () => {
        set({ error: null }, false, 'periodos/clearError');
      },
    }),
    { name: 'periodo-store' }
  )
);
